import { computed, nextTick, ref, Ref, watch, WatchStopHandle } from 'vue';
import { IMultiSelect } from '@studyportals/multiselect/dist/src/interfaces/multiselect.interface';
import { IOption } from '@studyportals/multiselect/dist/src/interfaces/options.interface';
import { DeliveryMode, DeliveryType } from '@studyportals/campaign-management-api-interface';
import { LoadingStatuses } from '@/models/loading.interface';
import { IInputStringEvent } from '@/models/input-events.interface'
import { ValidationProperties } from '@/models/validation.interface';
import { DEFAULT_OVER_PERFORMANCE_LIMIT } from '@/static/default-values';
import validationPresenter from '@/presenters/validation-presenter';
import store from '@/store';

export default class DeliveryModeCode {
	public deliveryModeDropdown: Ref<IMultiSelect | null> = ref(null);
	public options: IOption[] = [
		{ label: 'Evenly', value: DeliveryType.EVENLY },
		{ label: 'Custom overperformance limit', value: DeliveryType.CUSTOM_OVERPERFORMANCE }
	];
	private unwatch: WatchStopHandle | null = null;

	public overPerformanceLimit = computed((): number => {
		if (this.deliveryMode.value.type !== DeliveryType.CUSTOM_OVERPERFORMANCE) {
			return DEFAULT_OVER_PERFORMANCE_LIMIT;
		}

		return this.deliveryMode.value.overperformanceLimit;
	});

	public isDeliveryModeEvenly = computed((): boolean => {
		return this.deliveryMode.value.type === DeliveryType.EVENLY;
	});

	public isLoading = computed((): boolean => {
		const loadingStatus = store.getters.loadingStatusPrimaryData();
		return loadingStatus === LoadingStatuses.ACTIVE;
	});

	private deliveryMode = computed((): DeliveryMode => {
		return store.getters.deliveryMode();
	});

	public validationMessage = computed((): string => {
		if (this.isFieldValidatedAndInvalid.value) {
			return 'This field must be within the expected range';
		}

		return 'This field must be completed before proceeding';
	});

	public isFieldValidatedAndMissing = computed((): boolean => {
		return !this.isDeliveryModeEvenly.value
			&& validationPresenter.isPropertyMissing(ValidationProperties.DELIVERY_MODE);
	});

	public isFieldValidatedAndInvalid = computed((): boolean => {
		return !this.isDeliveryModeEvenly.value
			&& validationPresenter.isPropertyInvalid(ValidationProperties.DELIVERY_MODE);
	});

	public mounted(): void {
		this.unwatch = watch(this.isLoading, this.prefillDeliveryMode.bind(this));
		void this.prefillDeliveryMode();
	}

	public unmounted(): void {
		if (!this.unwatch) {
			return;
		}

		this.unwatch();
	}

	public setDeliveryMode(option: IOption): void {
		store.mutations.setDeliveryMode(option.value as DeliveryType);
	}

	public setOverPerformanceLimitBasedOnEvent(event: IInputStringEvent): void {
		const value = event?.target?.value;
		if (value === undefined) {
			return;
		}

		store.mutations.setOverPerformanceLimit(parseInt(value));
	}

	private async prefillDeliveryMode(): Promise<void> {
		if (this.isLoading.value) {
			return;
		}

		await nextTick();

		if (!this.deliveryModeDropdown.value) {
			return;
		}

		const selectedOption = this.options.find((option) => option.value === this.deliveryMode.value.type);
		if (!selectedOption) {
			return;
		}

		this.deliveryModeDropdown.value.selectOption(selectedOption);

		if (!this.isFieldValidatedAndMissing.value && !this.isFieldValidatedAndInvalid.value) {
			return;
		}

		validationPresenter.validateMandatoryFields();
	}
}
